// PWA → API compatibility contract.
//
// The PWA and the API deploy independently, so they agree on an integer
// `contract` version rather than a Git SHA. This module is the client half:
//
//   - it stamps every request to the account API origin with the client type,
//     the contract this build speaks, and the build id, so the API can fence an
//     out-of-date client with `426 Upgrade Required`;
//   - it polls `GET /v1/client-compatibility` (on boot, on foreground, and
//     after a 426) so the update screen appears WITHOUT first breaking a data
//     request;
//   - it owns the single `Update now` flow (service-worker update → cache-
//     busted reload) and the guard that stops reload loops.
//
// Scope is deliberately the API origin only: GitHub, the App runtime, analytics
// ingest, and every other fetch are untouched. The wrapper is installed once at
// boot because there are dozens of separate API clients; patching the global
// `fetch` is the one place that covers all of them without editing each.

import { accountApiBase, withConnectionAuth } from "./connection";

/** The contract version this build of the PWA understands. */
export const PWA_API_CONTRACT = 1;

/** Build identity injected at build time (`VITE_APP_VERSION`, a short SHA). */
export const PWA_BUILD = (() => {
  const raw = import.meta.env.VITE_APP_VERSION;
  const trimmed = typeof raw === "string" ? raw.trim() : "";
  return trimmed.length > 0 ? trimmed : "dev";
})();

const CLIENT_TYPE_HEADER = "X-Ato-Client-Type";
const CLIENT_CONTRACT_HEADER = "X-Ato-Client-Contract";
const CLIENT_BUILD_HEADER = "X-Ato-Client-Build";

const COMPATIBILITY_PATH = "/v1/client-compatibility";

/**
 * Minimum interval between foreground compatibility probes. Returning to a tab
 * fires `visibilitychange` often; a few minutes is frequent enough to catch a
 * deploy without turning every focus into a request.
 */
const MIN_CHECK_INTERVAL_MS = 2 * 60 * 1000;

const UPDATE_ATTEMPT_KEY = "ato.pwa.update_attempt_build";

/**
 * Compatibility is a HOSTED PWA concern. The bundled Desktop app is not updated
 * by a web deploy, has no service worker, and would reload the same bundled JS
 * — so an update gate there could never recover. Every entry point below is
 * disabled in the Desktop distribution.
 *
 * Resolved lazily (not captured at module load) so the build-time constant is
 * still testable via `vi.stubEnv`.
 */
export function isHostedPwaDistribution(): boolean {
  return import.meta.env.VITE_ATO_DISTRIBUTION !== "desktop";
}

export type PwaCompatibilityPhase =
  | "compatible"
  | "update_required"
  | "update_failed";

export interface PwaCompatibilityState {
  phase: PwaCompatibilityPhase;
  minApiContract: number | null;
}

let state: PwaCompatibilityState = {
  phase: "compatible",
  minApiContract: null,
};

const listeners = new Set<() => void>();

/** Stable snapshot for `useSyncExternalStore`. */
export function getPwaCompatibilityState(): PwaCompatibilityState {
  return state;
}

/** Subscribe to compatibility-state changes. Returns an unsubscribe function. */
export function subscribePwaCompatibility(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function setState(next: PwaCompatibilityState): void {
  state = next;
  for (const listener of listeners) listener();
}

function readAttemptedBuild(): string | null {
  try {
    return window.sessionStorage.getItem(UPDATE_ATTEMPT_KEY);
  } catch {
    return null;
  }
}

function markAttemptedBuild(build: string): void {
  try {
    window.sessionStorage.setItem(UPDATE_ATTEMPT_KEY, build);
  } catch {
    // Storage unavailable (private mode / sandbox): the in-memory reload still
    // happens once; only the loop guard degrades.
  }
}

/**
 * Record that the API requires a newer client.
 *
 * If an update was ALREADY attempted for exactly this build, the reload did not
 * actually deliver new code — surface a terminal "couldn't update, reload"
 * state instead of offering the same button that just looped.
 */
function noteUpdateRequired(minApiContract: number | null): void {
  if (state.phase === "update_failed") return;
  const attempted = readAttemptedBuild() === PWA_BUILD;
  setState({
    phase: attempted ? "update_failed" : "update_required",
    minApiContract: minApiContract ?? state.minApiContract,
  });
}

function resolveApiOrigin(): string | null {
  try {
    const base = accountApiBase();
    if (!base) return null;
    return new URL(base).origin;
  } catch {
    return null;
  }
}

function extractMinContract(body: unknown): number | null {
  if (!body || typeof body !== "object") return null;
  const value = (body as { min_pwa_contract?: unknown }).min_pwa_contract;
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

/**
 * The API uses `426` for other things too — e.g. `websocket_required` when a
 * plain fetch hits an activity route that needs an upgrade. Only the
 * compatibility fence's own body opens the update gate.
 */
function isUpdateRequiredBody(body: unknown): boolean {
  return (
    !!body &&
    typeof body === "object" &&
    (body as { error?: unknown }).error === "pwa_update_required"
  );
}

/**
 * A promise that never settles.
 *
 * A matching compatibility `426` is WITHHELD from the caller on purpose: the
 * point of the fence is that no existing API client ever interprets it, so it
 * must not reach them as a response or as a thrown error. The gate covers the
 * screen and the user reloads, at which point the pending request is discarded
 * with the page.
 */
function absorbed(): Promise<Response> {
  return new Promise<Response>(() => {});
}

/** Reads made while the first screen loads; the API reads their contract from the query. */
const QUERY_FENCED_READS = /^\/(?:api\/auth\/get-session|v1\/client-compatibility|v1\/discover\/.+)$/;

function withClientHeaders(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
): [RequestInfo | URL, RequestInit | undefined] {
  const headers = new Headers(input instanceof Request ? input.headers : undefined);
  new Headers(init?.headers).forEach((value, key) => headers.set(key, value));
  headers.set(CLIENT_TYPE_HEADER, "pwa");
  headers.set(CLIENT_CONTRACT_HEADER, String(PWA_API_CONTRACT));
  headers.set(CLIENT_BUILD_HEADER, PWA_BUILD);
  if (input instanceof Request) {
    return [new Request(input, { ...init, headers }), undefined];
  }
  return [input, { ...(init ?? {}), headers }];
}

let installed = false;
let nativeFetch: typeof fetch | null = null;

/**
 * Install the API-origin fetch compatibility layer. Idempotent. Must run before
 * the first API request fires (see `src/main.tsx`). No-op in the Desktop
 * distribution.
 */
export function installApiFetchCompatibility(): void {
  if (!isHostedPwaDistribution()) return;
  if (installed) return;
  if (typeof window === "undefined" || typeof window.fetch !== "function") return;
  installed = true;
  nativeFetch = window.fetch.bind(window);

  window.fetch = (async (input: RequestInfo | URL, init?: RequestInit) => {
    let target: URL | null = null;
    try {
      target = new URL(
        input instanceof Request ? input.url : String(input),
        window.location.href,
      );
    } catch {
      target = null;
    }

    const apiOrigin = resolveApiOrigin();
    if (!target || !apiOrigin || target.origin !== apiOrigin) {
      // Non-API origins are untouched — including any 426 they return.
      return nativeFetch!(input, init);
    }

    // Empty startup POSTs are CORS-safelisted. Preserve the same compatibility
    // fence via query metadata so these three diagnostic headers don't add an
    // OPTIONS round trip. Origin/CSRF/guest authority remain server-enforced.
    const method = (init?.method ?? (input instanceof Request ? input.method : "GET")).toUpperCase();
    // First-load reads get the same treatment: with no custom header they stay
    // CORS-simple, so the session and Discover reads skip a preflight.
    const startup = (method === "POST" &&
      /^\/v1\/discover\/(?:entry|apps\/[^/]+)\/start$/.test(target.pathname)) ||
      ((method === "GET" || method === "HEAD") && QUERY_FENCED_READS.test(target.pathname));
    let nextInput: RequestInfo | URL;
    let nextInit: RequestInit | undefined;
    if (startup) {
      target.searchParams.set("__ato_client_type", "pwa");
      target.searchParams.set("__ato_client_contract", String(PWA_API_CONTRACT));
      target.searchParams.set("__ato_client_build", PWA_BUILD);
      nextInput = input instanceof Request ? new Request(target, input) : target.toString();
      nextInit = init;
    } else [nextInput, nextInit] = withClientHeaders(input, init);
    const response = await nativeFetch!(nextInput, nextInit);

    if (response.status === 426) {
      const body = await response.clone().json().catch(() => null);
      if (isUpdateRequiredBody(body)) {
        noteUpdateRequired(extractMinContract(body));
        // Do not hand the compatibility 426 to any API client.
        return absorbed();
      }
    }
    return response;
  }) as typeof window.fetch;
}

let lastCheckAt = 0;
let inFlight: Promise<void> | null = null;

/**
 * Ask the API whether this build is still compatible.
 *
 * Skipped while an update is already required/failed, and rate-limited so a
 * burst of foreground events doesn't produce a burst of requests.
 */
export async function checkApiCompatibility(options?: {
  force?: boolean;
}): Promise<void> {
  if (!isHostedPwaDistribution()) return;
  if (state.phase !== "compatible") return;
  const now = Date.now();
  if (!options?.force && now - lastCheckAt < MIN_CHECK_INTERVAL_MS) return;
  if (inFlight) return inFlight;
  lastCheckAt = now;

  inFlight = (async () => {
    try {
      const base = accountApiBase();
      if (!base) return;
      const response = await window.fetch(
        `${base}${COMPATIBILITY_PATH}`,
        // Authenticate through the shared profile (cookie session hosted /
        // bearer local) rather than an inline credential — the same rule every
        // other API client follows.
        withConnectionAuth({ method: "GET", cache: "no-store" }),
      );
      if (!response.ok) return;
      const min = extractMinContract(await response.json());
      if (min !== null && PWA_API_CONTRACT < min) noteUpdateRequired(min);
    } catch {
      // A network failure is not an update signal.
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}

function waitForControllerChange(timeoutMs: number): Promise<void> {
  if (typeof navigator === "undefined" || !navigator.serviceWorker) {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    const timer = setTimeout(done, timeoutMs);
    function done() {
      clearTimeout(timer);
      navigator.serviceWorker.removeEventListener("controllerchange", done);
      resolve();
    }
    navigator.serviceWorker.addEventListener("controllerchange", done);
  });
}

/**
 * The `Update now` action: ask the service worker for a new build, wait briefly
 * for it to take control, then reload the current URL with a cache-buster.
 *
 * The reload is what swaps in the new bundle — the SW only makes sure the
 * navigation can't be served from an old shell. Desktop (no SW) still reloads.
 */
export async function requestPwaUpdate(): Promise<void> {
  if (!isHostedPwaDistribution()) return;
  // Recorded BEFORE reloading so the next boot can tell a failed update (same
  // build served again) from the first attempt.
  markAttemptedBuild(PWA_BUILD);

  try {
    const registration = await navigator.serviceWorker?.getRegistration();
    if (registration) {
      await registration.update();
      await waitForControllerChange(4000);
    }
  } catch {
    // SW unavailable or update failed — the cache-busted reload is still tried.
  }

  const url = new URL(window.location.href);
  url.searchParams.set("ato_update", Date.now().toString(36));
  window.location.replace(url.toString());
}

/** Test seam: restore the native fetch and clear module state. */
export function resetPwaCompatibilityForTests(): void {
  if (nativeFetch) {
    window.fetch = nativeFetch;
  }
  installed = false;
  nativeFetch = null;
  lastCheckAt = 0;
  inFlight = null;
  state = { phase: "compatible", minApiContract: null };
  listeners.clear();
  try {
    window.sessionStorage.removeItem(UPDATE_ATTEMPT_KEY);
  } catch {
    // ignore
  }
}
